import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { FACILITY_KINDS, type Facility, type FacilityKind, type Page, type SlotsListResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** GET /facilities (directory) + POST /facilities (register) + slot management — packages/modules/m24-ehealth/src/routes/facilities.ts. */
export function Facilities() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('RECEPTION') ?? false;
  const [district, setDistrict] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Facility | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const qs = new URLSearchParams({ limit: '100' });
  if (district) qs.set('district', district);
  if (kind) qs.set('kind', kind);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<Facility>>(`/api/v1/m24/facilities?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Facility Directory"
        subtitle="Clinics, hospitals and lab partners bookable across the eHealth system."
        actions={
          canManage ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register facility
            </button>
          ) : undefined
        }
      />

      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Registering a facility or adding slots requires the RECEPTION role — the directory below is visible to everyone." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-56" placeholder="Search name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input !w-44" placeholder="District…" value={district} onChange={(e) => setDistrict(e.target.value)} />
        <select className="field-input !w-44" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All kinds</option>
          {FACILITY_KINDS.map((k) => (
            <option key={k} value={k}>
              {k.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          {listQ.loading ? (
            <Spinner />
          ) : (listQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No facilities match" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Kind</th>
                  <th className="table-head">District</th>
                  <th className="table-head">Specialties</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data!.items.map((f) => (
                  <tr
                    key={f.id}
                    className={`cursor-pointer hover:bg-slate-50 ${selected?.id === f.id ? 'bg-brand-50' : ''}`}
                    onClick={() => setSelected(f)}
                  >
                    <td className="table-cell font-medium">{f.name}</td>
                    <td className="table-cell">
                      <StatusBadge status={f.kind} />
                    </td>
                    <td className="table-cell">{f.district}</td>
                    <td className="table-cell text-xs text-slate-500">{f.specialties.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected ? (
          <SlotsPanel facility={selected} canManage={canManage} />
        ) : (
          <div className="card p-5">
            <EmptyState title="Select a facility" hint="Choose a row on the left to view or add bookable slots." />
          </div>
        )}
      </div>

      {showCreate ? (
        <CreateFacilityModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SlotsPanel({ facility, canManage }: { facility: Facility; canManage: boolean }) {
  const [showAll, setShowAll] = useState(false);
  const slotsQ = useApiQuery<SlotsListResponse>(`/api/v1/m24/facilities/${facility.id}/slots?all=${showAll}`);
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div className="card p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">{facility.name} — bookable slots</h2>
        {canManage ? (
          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAdd(true)}>
            + Add slot
          </button>
        ) : null}
      </div>
      <label className="mb-3 flex items-center gap-1.5 text-xs text-slate-500">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
        Include past / fully-booked slots
      </label>

      {slotsQ.error ? <ErrorNote message={slotsQ.error} /> : null}
      {slotsQ.loading ? (
        <Spinner />
      ) : (slotsQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No slots" hint={showAll ? undefined : 'No open slots — try including past/full slots.'} />
      ) : (
        <ul className="space-y-1.5">
          {slotsQ.data!.items.map((s) => (
            <li key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <div>
                <div className="font-medium text-slate-800">
                  {s.specialty} {s.teleconsult ? <span className="ml-1 text-xs text-indigo-600">(teleconsult)</span> : null}
                </div>
                <div className="text-xs text-slate-500">{formatDateTime(s.startsAt)} · {s.durationMinutes} min</div>
              </div>
              <div className="text-xs font-semibold tabular-nums text-slate-600">
                {s.spotsRemaining ?? s.capacity - s.bookedCount}/{s.capacity} open
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd ? (
        <AddSlotModal
          facility={facility}
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            slotsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateFacilityModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [district, setDistrict] = useState('');
  const [kind, setKind] = useState<FacilityKind>('CLINIC');
  const [address, setAddress] = useState('');
  const [specialtiesInput, setSpecialtiesInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m24/facilities', {
        name,
        district,
        kind,
        address: address || undefined,
        specialties: specialtiesInput
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register facility');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register facility" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anse Royale Hospital" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Anse Royale" />
          </Field>
          <Field label="Kind">
            <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as FacilityKind)}>
              {FACILITY_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Address (optional)">
          <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label="Specialties (comma separated)">
          <input className="field-input" value={specialtiesInput} onChange={(e) => setSpecialtiesInput(e.target.value)} placeholder="e.g. General Medicine, Paediatrics" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2 || district.trim().length < 2}>
            {busy ? 'Registering…' : 'Register facility'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddSlotModal({ facility, onClose, onDone }: { facility: Facility; onClose: () => void; onDone: () => void }) {
  const [specialty, setSpecialty] = useState(facility.specialties[0] ?? '');
  const [startsAt, setStartsAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('30');
  const [capacity, setCapacity] = useState('1');
  const [teleconsult, setTeleconsult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m24/facilities/${facility.id}/slots`, {
        specialty,
        startsAt: new Date(startsAt).toISOString(),
        durationMinutes: Number.parseInt(durationMinutes, 10),
        capacity: Number.parseInt(capacity, 10),
        teleconsult,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add slot');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add slot — ${facility.name}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Specialty">
          <input className="field-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="e.g. General Medicine" />
        </Field>
        <Field label="Starts at">
          <input type="datetime-local" className="field-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Duration (minutes)">
            <input type="number" min="5" max="480" className="field-input" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} />
          </Field>
          <Field label="Capacity">
            <input type="number" min="1" max="50" className="field-input" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={teleconsult} onChange={(e) => setTeleconsult(e.target.checked)} />
          Teleconsult slot (issues a video session link on booking)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !specialty.trim() || !startsAt}>
            {busy ? 'Adding…' : 'Add slot'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
